import { type StateBuilder } from '@atlassian/state-machine/builder/types';
import {
  type BaseContext,
  type BaseEvent,
  type BaseEventLookup,
  type EventProducer,
} from '@atlassian/state-machine/types';

export interface RejectedEvent {
  type: 'rejected';
  error: Error;
}

export function asRejectEvent(error: Error) {
  return { type: `rejected`, error } as RejectedEvent;
}

interface ContextWithAbortSignal extends BaseContext {
  signal: AbortSignal;
}

/**
 * Construct an async state
 * @param asyncTask The async task to execute, it gets the context and an abort signal - and resolve to an event
 * @param onError An error handler
 * @param builder A builder to add state transitions to the state
 */
export function asyncState<
  S extends string,
  E extends BaseEvent,
  L extends BaseEventLookup<E['type']>,
  C extends ContextWithAbortSignal,
  R extends E,
  Err extends E
>(
  asyncTask: (ctx: C, abort: AbortSignal) => Promise<R>,
  onError: (error: Error) => Err,
  builder: (s: StateBuilder<S, E, L, C>) => StateBuilder<S, E, L, C>
): (s: StateBuilder<S, E, L, C>) => StateBuilder<S, E, L, C> {
  const symbol = Symbol();

  return (s) => {
    const events = s
      .on('enter', (ctx: C, produce: EventProducer<E>) => {
        if (ctx.signal.aborted) {
          return;
        }

        const actionAborted = new AbortController();

        // We attach to the context with a private symbol as this is an internally managed context value
        // Multiple async states can be nested, and these shouldn't conflict
        (ctx as any)[symbol] = actionAborted;

        // If the top level signal aborts, then we abort this signal
        ctx.signal.addEventListener(
          'abort',
          () => {
            actionAborted.abort();
          },
          { signal: actionAborted.signal }
        );

        // Catch synchronous errors and async errors
        try {
          asyncTask(ctx, actionAborted.signal).then(
            (res) => {
              // Only produce if both this result is valid - and the whole state machine has not been aborted
              if (!actionAborted.signal.aborted && !ctx.signal.aborted) {
                produce(res);
              }
            },
            (err) => {
              // Only produce if both this error is valid - and the whole state machine has not been aborted
              if (!actionAborted.signal.aborted && !ctx.signal.aborted) {
                return produce(onError(err));
              }
            }
          );
        } catch (error: any) {
          // Only produce if the state machine was not in an abort state
          if (!ctx.signal.aborted) {
            produce(onError(error));
          }
        }
      })
      .on('exit', (ctx: C) => {
        (ctx as any)[symbol]?.abort();
        delete (ctx as any)[symbol];
      });

    builder(events);

    return events;
  };
}
